import { useState } from 'react'
import { ArrowRightIcon, Button, MinusIcon, Paragraph } from '@quickmd-llc/design-system'
import { ADMIN_EVENT_NAMES, tracking } from '@/lib/segment/tracking'
import { type Patient } from '@/types/patient'
import { useMergeAccounts } from './context/useMergeAccounts'
import { AccountCard } from './AccountCard'
import { pluralize } from '@quickmd-llc/frontend-utils'

function EmptyState() {
  return (
    <div className='flex flex-1 items-center justify-center p-sp-2'>
      <div className='flex flex-col items-center gap-sp-1 text-center'>
        <ArrowRightIcon iconSize='xxl' className='text-text-light' />
        <Paragraph className='font-semibold text-text-dark'>No accounts selected</Paragraph>
        <Paragraph>Select accounts from the left to begin merging.</Paragraph>
      </div>
    </div>
  )
}

export function SelectedAccounts() {
  const { removePatients, selectedPatients } = useMergeAccounts()
  const [patientsToRemove, setPatientsToRemove] = useState<Array<Patient>>([])

  function handleClickAccountCard(patient: Patient) {
    if (!patientsToRemove.find((p) => p.PatientId === patient.PatientId)) {
      tracking.trackEvent(ADMIN_EVENT_NAMES.MERGE_ACCOUNTS_MERGE_LIST_SELECT_ACCOUNT)

      setPatientsToRemove((prev) => [...prev, patient])
    } else {
      setPatientsToRemove((prev) => prev.filter((p) => p.PatientId !== patient.PatientId))
    }
  }

  function handleClickRemoveFromList() {
    tracking.trackEvent(ADMIN_EVENT_NAMES.MERGE_ACCOUNTS_MERGE_LIST_REMOVE_FROM_LIST)

    removePatients(patientsToRemove)
    setPatientsToRemove([])
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col rounded-lg border border-border-dark bg-white'>
      <div className='grid gap-sp-2 p-sp-2'>
        <div className='flex items-start justify-between'>
          <div>
            <Paragraph className='text-dark font-semibold'>Merge list</Paragraph>
            <Paragraph>
              {selectedPatients.length} {pluralize('account', selectedPatients.length)} ready for merging
            </Paragraph>
          </div>

          <Button
            size='s'
            className='flex-shrink-0'
            onClick={() => handleClickRemoveFromList()}
            disabled={patientsToRemove.length === 0}>
            <MinusIcon />
            Remove from list
          </Button>
        </div>

        <Paragraph className='text-text-light' size='sm'>
          Once you have accounts ready to merge, click Merge accounts below to continue. In the next step, you’ll choose
          a main account and assign its status.
        </Paragraph>
      </div>

      {selectedPatients.length > 0 ?
        <div className='overflow-y-auto p-sp-2'>
          <div className='grid gap-sp-2'>
            {selectedPatients.map((patient) => (
              <AccountCard
                key={patient.PatientId}
                patient={patient}
                onClick={(p) => handleClickAccountCard(p)}
                isSelected={patientsToRemove.includes(patient)}
              />
            ))}
          </div>
        </div>
      : <EmptyState />}
    </div>
  )
}
